import { Head, useForm } from '@inertiajs/react'
import type { ReactNode } from 'react'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { AppLayout } from '@/layouts/AppLayout'
import { userPasswordPath } from '@/lib/routes'

type ResetPasswordProps = {
  minimum_password_length?: number
  reset_password_token: string
}

function ResetPassword({
  minimum_password_length: minimumPasswordLength,
  reset_password_token: resetPasswordToken,
}: ResetPasswordProps) {
  const form = useForm({
    reset_password_token: resetPasswordToken,
    password: '',
    password_confirmation: '',
  })

  return (
    <>
      <Head title="Choose a new password" />
      <div className="mx-auto flex min-h-[calc(100vh-4rem)] max-w-md items-center px-4 py-10">
        <Card className="w-full">
          <CardHeader className="space-y-1">
            <CardTitle className="text-2xl">Choose a new password</CardTitle>
            <CardDescription>
              {minimumPasswordLength
                ? `At least ${minimumPasswordLength} characters.`
                : 'Use something strong you can remember.'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form
              className="space-y-4"
              onSubmit={(event) => {
                event.preventDefault()
                form.transform((data) => ({ user: data }))
                form.put(userPasswordPath())
              }}
            >
              <div className="space-y-1.5">
                <Label htmlFor="password">New password</Label>
                <Input
                  autoComplete="new-password"
                  autoFocus
                  id="password"
                  onChange={(event) => form.setData('password', event.target.value)}
                  type="password"
                  value={form.data.password}
                />
                {form.errors.password && (
                  <p className="text-xs text-destructive">{form.errors.password}</p>
                )}
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="password-confirmation">Confirm password</Label>
                <Input
                  autoComplete="new-password"
                  id="password-confirmation"
                  onChange={(event) => form.setData('password_confirmation', event.target.value)}
                  type="password"
                  value={form.data.password_confirmation}
                />
                {form.errors.password_confirmation && (
                  <p className="text-xs text-destructive">{form.errors.password_confirmation}</p>
                )}
              </div>

              <Button className="w-full" disabled={form.processing} type="submit">
                {form.processing ? 'Saving…' : 'Update password'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </>
  )
}

export default Object.assign(ResetPassword, {
  layout: (page: ReactNode) => <AppLayout>{page}</AppLayout>,
})
